import React, { PropsWithChildren, useMemo } from 'react';
import { StepType, TourProvider } from '../Tour/tour';
import { OnboardingTourItem } from '../OnboardingTourItem/OnboardingTourItem';
import { DealFinder, CommunityIcon, CoachingIcon } from '../../assets';
import { useDefaultUserStore } from '../../store';
import {
	hasCoachingAccess,
	hasCommunityAccess,
	hasDealFinderGuideAccess,
} from '../../utils';

export const OnboardingTourProvider: React.FC<PropsWithChildren> = ({
	children,
}) => {
	const { user } = useDefaultUserStore();
	const hasCoaching = hasCoachingAccess(user?.claimsIds);
	const hasCommunity = hasCommunityAccess(user?.claimsIds);
	const hasDealFinderGuide = hasDealFinderGuideAccess(
		user?.claimsIds,
		user?.modulesIds,
	);

	const tourSteps = useMemo<StepType[]>(() => {
		const steps: StepType[] = [];

		if (hasDealFinderGuide) {
			steps.push({
				selector: '.guide-deal-finder',
				content: (
					<OnboardingTourItem
						title="Deal Finder"
						content="This tab will be used to source homes, analyze deals, and find cash flowing gold mines."
						icon={<DealFinder />}
						index={steps.length}
					/>
				),
				position: [266, 128],
			});
		}

		if (hasCommunity) {
			steps.push({
				selector: '.guide-community',
				content: (
					<OnboardingTourItem
						title="Community"
						content="This tab is where you’ll go to connect with other likeminded investors to discuss Section 8 deals, ideas, and to consult with each other in general."
						icon={<CommunityIcon />}
						index={steps.length}
					/>
				),
				position: [266, 450],
			});
		}

		if (hasCoaching) {
			steps.push({
				selector: '.guide-coaching',
				content: (
					<OnboardingTourItem
						title="1 on 1 With Tom"
						content="This is where you’ll work directly with Tom to secure Section 8 Properties."
						icon={<CoachingIcon />}
						index={steps.length}
						shouldOpenTomChat={true}
					/>
				),
				position: [266, 520],
			});
		}

		return steps;
	}, [hasCoaching, hasCommunity, hasDealFinderGuide]);

	return (
		<TourProvider
			steps={tourSteps}
			position="right"
			padding={0}
			onClickMask={({ setIsOpen, setCurrentStep }) => {
				setIsOpen(false);
				setTimeout(() => {
					setCurrentStep(0);
				}, 400);
			}}
			styles={{
				maskArea: base => ({ ...base, rx: 10 }),
				popover: base => ({
					...base,
					padding: 0,
					borderRadius: '17px',
					borderTopLeftRadius: 0,
					boxShadow: '4px 12px 10px 0px #00000052',
				}),
			}}>
			{children}
		</TourProvider>
	);
};
